// =============================
// src/pages/HomePage.js (copy‑paste)
// Dashboard with top tiles, Most Used Documents, Clients list, and Dev Inbox (dev-only)
// =============================
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import DeveloperInboxPanel from '../components/DeveloperInboxPanel';

export default function HomePage() {
  const { user, isDev, loadingAuth } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topDocs, setTopDocs] = useState([]);
  const [devNotes, setDevNotes] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        // Profile
        const snap = await getDoc(doc(db, 'users', user.uid));
        const prof = snap.exists() ? (snap.data() || {}) : {};
        setProfile(prof);

        // Top docs usage (subcollection: users/{uid}/stats/docUsage)
        const usageCol = collection(db, 'users', user.uid, 'stats', 'docUsage');
        const usageQ = query(usageCol, orderBy('count', 'desc'), limit(8));
        const usageSnap = await getDocs(usageQ);
        const items = usageSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setTopDocs(items);

        // Dev inbox (if developer)
        if (prof.role === 'Developer') {
          const inboxQ = query(collection(db, 'devInbox'), orderBy('createdAt', 'desc'), limit(20));
          const inboxSnap = await getDocs(inboxQ);
          setDevNotes(inboxSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } else {
          setDevNotes([]);
        }
      } catch (e) {
        console.warn('Home load failed:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.uid]);

  const clients = useMemo(() => Array.isArray(profile?.clients) ? profile.clients : [], [profile]);

  const totalVehicles = useMemo(() => {
    if (Number.isFinite(profile?.totalVehicles)) return profile.totalVehicles;
    return clients.reduce((sum, c) => {
      const n = parseInt((c?.vehicleCount || '').toString().replace(/\D/g, ''), 10);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }, [profile, clients]);

  const nearLimit = totalVehicles >= 45 && totalVehicles < 50;
  const atLimit = totalVehicles >= 50;

  const openFromHome = (item) => {
    // Store intent and navigate to Documents page; DocumentsPage will read and open
    try {
      localStorage.setItem('openNextDoc', JSON.stringify({ key: item.id, title: item.title, kind: item.kind }));
    } catch {}
    navigate('/documents');
  };

  if (loadingAuth || loading) return <div className="p-6">Loading dashboard…</div>;
  if (!user) return <div className="p-6">Please sign in to view your dashboard.</div>;

  const lastSaved = profile?.updatedAt?.toDate ? profile.updatedAt.toDate() : null;

  return (
    <div className="p-6 space-y-6">
      {/* Top tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 bg-white border rounded">
          <div className="text-sm text-gray-600">Total Clients</div>
          <div className="text-2xl font-semibold">{clients.length}</div>
        </div>
        <div className={`p-4 bg-white border rounded ${atLimit ? 'ring-2 ring-red-500' : nearLimit ? 'ring-2 ring-amber-500' : ''}`}>
          <div className="text-sm text-gray-600">Total Vehicles</div>
          <div className="text-2xl font-semibold">{totalVehicles} / 50</div>
          {nearLimit && !atLimit && <div className="text-xs text-amber-700 mt-1">Close to legal limit.</div>}
          {atLimit && <div className="text-xs text-red-700 mt-1">At legal limit.</div>}
        </div>
        <div className="p-4 bg-white border rounded">
          <div className="text-sm text-gray-600">Signed in</div>
          <div className="text-base">{profile?.name || user.displayName || '(No name)'}</div>
          <div className="text-xs text-gray-600">{user.email || '(No email)'}</div>
        </div>
        <div className="p-4 bg-white border rounded">
          <div className="text-sm text-gray-600">Last Saved</div>
          <div className="text-base">{lastSaved ? lastSaved.toLocaleString('en-GB') : '—'}</div>
        </div>
      </div>

      {/* Most Used Documents */}
      <section className="bg-white border rounded p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-semibold">Most Used Documents</h3>
          <button onClick={() => navigate('/documents')} className="text-blue-600 hover:underline">Open Documents</button>
        </div>
        {topDocs.length === 0 ? (
          <div className="text-sm text-gray-600">
            No usage yet. Open a document from the Documents page and it will start appearing here.
          </div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {topDocs.map(item => (
              <li key={item.id} className="border rounded p-3 flex items-center justify-between">
                <div>
                  <div className="font-medium">{item.title || item.id}</div>
                  <div className="text-xs text-gray-600">{item.kind || 'document'} • used {item.count || 0} time(s)</div>
                </div>
                <button
                  className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                  onClick={() => openFromHome(item)}
                >
                  Open
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Clients mini list */}
      <section className="bg-white border rounded p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-semibold">Your Clients</h3>
          <button onClick={() => navigate('/profile')} className="text-blue-600 hover:underline">Manage clients</button>
        </div>
        {clients.length === 0 ? (
          <div className="text-sm text-gray-600">No clients saved yet. Add them on the Profile page.</div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {clients.slice(0, 8).map((c, i) => (
              <li key={i} className="border rounded p-3">
                <div className="font-medium">{c.companyName || '(Unnamed client)'}</div>
                <div className="text-xs text-gray-600">{c.address || ''}</div>
              </li>
            ))}
          </ul>
        )}
        {clients.length > 8 && (
          <div className="text-xs text-gray-600 mt-2">Showing 8 of {clients.length} clients.</div>
        )}
      </section>

      {/* Developer Inbox (dev-only) */}
      {!loadingAuth && isDev && (
        <DeveloperInboxPanel notes={devNotes} />
      )}
    </div>
  );
}